/**
 * Centralized CPF/CNPJ value object utilities.
 * All formatting, cleaning, and detection rules live here.
 */

const MASK_CHARS = /[.\-/\s]/g;

/**
 * MaskEdit mask of a CNPJ (alphanumeric): `*` takes a letter or digit, `_` a check digit.
 * The keys are not CNPJ characters because @react-input/mask ignores a typed key (a key `A` drops the letter A).
 */
export const CNPJ_MASK = '**.***.***/****-__';

export const CNPJ_MASK_REPLACEMENT: Record<string, RegExp> = { '*': /[A-Za-z0-9]/, _: /\d/ };

/**
 * Keep digits only.
 *
 * Lives here, in a dependency-free util, and not in PayerFields: importing it from a component
 * module dragged that component's whole tree (axios, aws-amplify) into anything that only needed
 * the helper — which broke the payload builder's test suite under the pipeline's jest config.
 */
export const onlyDigits = (value: string): string => (value || '').replace(/\D/g, '');

/** Remove mask characters and uppercase */
export const cleanTaxId = (value: string): string =>
    value.replace(MASK_CHARS, '').toUpperCase();

/** Detect if cleaned value is CPF */
export const isCpf = (cleaned: string): boolean =>
    cleaned.length === 11 && /^\d{11}$/.test(cleaned);

/** Detect if cleaned value is full CNPJ (alphanumeric) */
export const isCnpj = (cleaned: string): boolean =>
    cleaned.length === 14 && /^[A-Z0-9]{12}[0-9]{2}$/i.test(cleaned);

/** Detect if cleaned value is CNPJ root */
export const isCnpjRoot = (cleaned: string): boolean =>
    cleaned.length === 8 && /^[A-Z0-9]{8}$/i.test(cleaned);

/** Format CPF: 999.999.999-99 */
export const formatCpf = (cleaned: string): string =>
    `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6, 9)}-${cleaned.slice(9)}`;

/** Format CNPJ: XX.XXX.XXX/XXXX-XX (supports alphanumeric) */
export const formatCnpj = (cleaned: string): string =>
    `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8, 12)}-${cleaned.slice(12)}`;

/** Format CPF or CNPJ for display */
export const formatTaxId = (value: string): string => {
    if (!value) return '';
    const cleaned = cleanTaxId(value);
    if (isCpf(cleaned)) return formatCpf(cleaned);
    if (isCnpj(cleaned)) return formatCnpj(cleaned);
    return value;
};

/** Detect document type from raw or cleaned value */
export const detectTaxIdType = (value: string): 'CPF' | 'CNPJ' | 'CNPJ_ROOT' | null => {
    const cleaned = cleanTaxId(value);
    if (isCpf(cleaned)) return 'CPF';
    if (isCnpj(cleaned)) return 'CNPJ';
    if (isCnpjRoot(cleaned)) return 'CNPJ_ROOT';
    return null;
};
